import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Dimensions } from 'react-native';
import { WebView } from 'react-native-webview';
import * as FileSystem from 'expo-file-system';
import { logger } from '../utils/logger';
import { generateHotspotPopupContent } from '../utils/mapPopupTemplates';

interface LeafletMapWithOverlayProps {
  center: { lat: number; lng: number };
  zoom: number;
  markers: Array<{
    position: { lat: number; lng: number };
    icon: string;
    size: [number, number];
    title: string;
    temperature?: number;
    chlorophyll?: number;
    currentSpeed?: number;
    currentDirection?: number;
    ssh?: number;
    type?: string;
    noCluster?: boolean;
    hotspotNumber?: number;
    confidence?: number;
    score?: number;
    reasons?: string[];
    gradients?: string[];
    nearbyStructures?: any[];
    buoyData?: any; // Add buoyData property for buoy markers
    description?: string; // Add description for waypoint markers
    category?: string; // Add category for waypoint markers
  }>;
  overlays?: Array<{
    url: string;
    bounds: {
      minLat: number;
      maxLat: number;
      minLon: number;
      maxLon: number;
    };
    zIndex?: number;
    type?: 'image' | 'tile' | 'custom-bag' | 'custom-multibeam'; // Added to support tile layers and custom bathymetry
    opacity?: number; // Added to support opacity control
  }>;
  shapes?: Array<{
    shapeType: 'polyline';
    positions: Array<{ lat: number; lng: number }>;
    color?: string;
    weight?: number;
    distance?: number;
  }>;
  onMapClick?: (lat: number, lng: number) => void;
  onMapLongPress?: (lat: number, lng: number) => void;
  onMarkerClick?: (position: { lat: number; lng: number }) => void;
  onMapMove?: (center: { lat: number; lng: number }, zoom: number, bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number }) => void;
  enableClustering?: boolean; // Enable marker clustering
  baseMapType?: 'street' | 'ocean'; // Add support for different base maps
}

export const LeafletMapWithOverlay: React.FC<LeafletMapWithOverlayProps> = ({
  center,
  zoom,
  markers,
  overlays = [],
  shapes = [],
  onMapClick,
  onMapLongPress,
  onMarkerClick,
  onMapMove,
  enableClustering = false,
  baseMapType = 'street'
}) => {
  const webViewRef = useRef<WebView>(null);
  
  // Ensure we have valid center coordinates
  const safeCenter = center && center.lat !== undefined && center.lng !== undefined 
    ? center 
    : { lat: 25.0, lng: -90.0 }; // Default to Gulf of Mexico
    
  // Ensure we have a valid zoom level
  const safeZoom = zoom !== undefined && zoom !== null ? zoom : 8;

  useEffect(() => {
    const updateOverlays = async () => {
      if (!webViewRef.current) return;
      
      // Base map stays constant - we always use the ocean base map for a fishing app
      const baseMapScript = `
        (function() {
          var currentBaseMapType = window.currentBaseMapType || 'ocean';
          
          // Only switch if we haven't set up the base map yet
          if (currentBaseMapType !== 'ocean' || !window.baseLayer) {
            if (window.baseLayer) {
              map.removeLayer(window.baseLayer);
            }
            
            // Use Esri Ocean base map
            window.baseLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}', {
              maxZoom: 20,
              maxNativeZoom: 13,
              attribution: 'Esri, GEBCO, NOAA, National Geographic, DeLorme, HERE, Geonames.org'
            }).addTo(map);
            
            // Add Ocean Reference overlay with maritime features, cities, and offshore names
            window.labelsLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Reference/MapServer/tile/{z}/{y}/{x}', {
              maxZoom: 20,
              maxNativeZoom: 13,
              opacity: 1.0,
              zIndex: 1000
            }).addTo(map);
            
            map.setMaxZoom(20);
            
            window.currentBaseMapType = 'ocean';
          }
        })();
      `;
      webViewRef.current.injectJavaScript(baseMapScript);
      
      // Process overlays to handle local files
      const processedOverlaysPromises = overlays.map(async (overlay, index) => {
        // Check if it's a local file URL
        if (overlay.url.startsWith('file://') || overlay.url.startsWith('/')) {
          try {
            // Handle both file:// URLs and direct paths
            const localPath = overlay.url.startsWith('file://') 
              ? overlay.url.replace('file://', '') 
              : overlay.url;
            
            // Check if file exists
            const fileInfo = await FileSystem.getInfoAsync(localPath);
            if (!fileInfo.exists) {
              logger.error('Local file does not exist', new Error('File not found'), { path: localPath });
              return null;
            }
            
            // Read the local file and convert to base64
            const base64 = await FileSystem.readAsStringAsync(localPath, {
              encoding: FileSystem.EncodingType.Base64,
            });
            const dataUrl = `data:image/png;base64,${base64}`;
            logger.info('Converted local file to data URL', { 
              originalUrl: overlay.url, 
              localPath,
              fileSize: fileInfo.size,
              dataUrlLength: dataUrl.length 
            });
            return { ...overlay, url: dataUrl, index };
          } catch (error) {
            logger.error('Failed to read local file for overlay', error instanceof Error ? error : new Error(String(error)), { 
              url: overlay.url,
              errorMessage: error instanceof Error ? error.message : String(error)
            });
            return null;
          }
        }
        return { ...overlay, index };
      });
      
      const processedOverlays = (await Promise.all(processedOverlaysPromises)).filter((overlay): overlay is NonNullable<typeof overlay> => overlay !== null && overlay !== undefined);
      
      // Validate URLs before processing
      processedOverlays.forEach((overlay, idx) => {
        if (!overlay.url || overlay.url.length === 0) {
          logger.error('Invalid overlay URL detected', new Error('Empty URL'), { index: idx });
        } else {
          logger.info('Processing overlay', { 
            index: idx, 
            urlLength: overlay.url.length,
            urlStart: overlay.url.substring(0, 100),
            type: overlay.type,
            bounds: overlay.bounds
          });
        }
      });
      
      const script = `
        // Check for existing bathymetry layer
        var existingBathymetry = null;
        if (window.overlays) {
          // Find and preserve bathymetry layer if it exists
          window.overlays.forEach(function(overlay) {
            if (overlay.isCustomTileLayer) {
              existingBathymetry = overlay;
            } else {
              // Remove non-bathymetry overlays
              overlay.remove();
            }
          });
          
          // Reset overlays array but keep bathymetry if found
          window.overlays = existingBathymetry ? [existingBathymetry] : [];
        } else {
          window.overlays = [];
        }
        
        // Check if we need bathymetry layer
        var needsBathymetry = ${processedOverlays.some(o => o.type === 'custom-bag' || o.type === 'custom-multibeam' || o.url === 'NOAA_BAG_BATHYMETRY' || o.url === 'MULTIBEAM_BATHYMETRY')};
        
        // Remove bathymetry if no longer needed
        if (!needsBathymetry && existingBathymetry) {
          existingBathymetry.remove();
          window.overlays = [];
          existingBathymetry = null;
        }
        
        // Add new overlays
        ${processedOverlays.map((overlay) => {
          // Validate URL first
          if (!overlay.url || overlay.url.length === 0) {
            console.error('Skipping overlay with empty URL at index', overlay.index);
            return '';
          }
          
          // Handle local file URLs differently - escape properly for JavaScript string
          // Use JSON.stringify for proper escaping
          const escapedUrl = JSON.stringify(overlay.url).slice(1, -1);
          
          if (overlay.type === 'custom-bag' || overlay.url === 'NOAA_BAG_BATHYMETRY') {
            // Handle NOAA BAG Bathymetry with custom tile generation
            return `
            // Skip if bathymetry layer already exists
            if (!existingBathymetry) {
              console.log('Adding NOAA BAG bathymetry layer');
            var ArcGISTileLayer${overlay.index} = L.TileLayer.extend({
              getTileUrl: function (coords) {
                var tileSize = this.options.tileSize;
                var nwPoint = coords.scaleBy(L.point(tileSize, tileSize));
                var sePoint = nwPoint.add([tileSize, tileSize]);
                var nw = map.unproject(nwPoint, coords.z);
                var se = map.unproject(sePoint, coords.z);
                // Correct bbox order: minX,minY,maxX,maxY (west,south,east,north)
                var bbox = nw.lng + ',' + se.lat + ',' + se.lng + ',' + nw.lat;
                // Add spatial reference for Web Mercator
                var srs = '&bboxSR=4326&imageSR=3857';
                return 'https://gis.ngdc.noaa.gov/arcgis/rest/services/bag_bathymetry/ImageServer/exportImage?bbox=' + bbox + '&size=256,256&format=png32&f=image' + srs + '&renderingRule={"rasterFunction":"ColorHillshadeBAG"}';
              },
              _onZoomEnd: function() {
                // Force complete refresh on zoom end
                this._removeAllTiles();
                this._update();
              }
            });
            
            var overlay${overlay.index} = new ArcGISTileLayer${overlay.index}('', {
              tileSize: 256,
              opacity: ${overlay.opacity || 0.7},
              attribution: 'NOAA BAG Bathymetry',
              zIndex: ${overlay.zIndex || overlay.index},
              minZoom: 1,
              maxZoom: 20,  // Allow maximum zoom
              maxNativeZoom: 19,  // NOAA BAG supports up to zoom 19
              updateWhenZooming: true,  // Update tiles while zooming
              updateWhenIdle: true,
              keepBuffer: 1,  // Keep 1 tile buffer for smoother panning
              errorTileUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
            }).addTo(map);
            
            // Add error handling
            overlay${overlay.index}.on('tileerror', function(error) {
              console.error('BAG tile error:', error.tile._url);
            });
            overlay${overlay.index}.isCustomTileLayer = true; // Mark as custom tile layer
            window.overlays.push(overlay${overlay.index});
            }
            `;
          } else if (overlay.type === 'custom-multibeam' || overlay.url === 'MULTIBEAM_BATHYMETRY') {
            // Handle Multibeam Bathymetry Mosaic
            return `
            // Skip if bathymetry layer already exists
            if (!existingBathymetry) {
              console.log('Adding Multibeam Bathymetry Mosaic layer');
            var MultibeamTileLayer${overlay.index} = L.TileLayer.extend({
              getTileUrl: function (coords) {
                var tileSize = this.options.tileSize;
                var nwPoint = coords.scaleBy(L.point(tileSize, tileSize));
                var sePoint = nwPoint.add([tileSize, tileSize]);
                var nw = map.unproject(nwPoint, coords.z);
                var se = map.unproject(sePoint, coords.z);
                // Correct bbox order: minX,minY,maxX,maxY (west,south,east,north)
                var bbox = nw.lng + ',' + se.lat + ',' + se.lng + ',' + nw.lat;
                // Add spatial reference for Web Mercator
                var srs = '&bboxSR=4326&imageSR=3857';
                // Use Multibeam Bathymetry Mosaic service layer
                return 'https://gis.ngdc.noaa.gov/arcgis/rest/services/multibeam_mosaic/ImageServer/exportImage?bbox=' + bbox + '&size=256,256&format=png32&f=image' + srs;
              },
              _onZoomEnd: function() {
                // Force complete refresh on zoom end
                this._removeAllTiles();
                this._update();
              }
            });
            
            var overlay${overlay.index} = new MultibeamTileLayer${overlay.index}('', {
              tileSize: 256,
              opacity: ${overlay.opacity || 0.8},
              attribution: 'NOAA Multibeam Bathymetry',
              zIndex: ${overlay.zIndex || overlay.index},
              minZoom: 1,
              maxZoom: 20,  // Allow maximum zoom
              maxNativeZoom: 19,  // Multibeam supports up to zoom 19
              updateWhenZooming: true,  // Update tiles while zooming
              updateWhenIdle: true,
              keepBuffer: 1,  // Keep 1 tile buffer for smoother panning
              errorTileUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
            }).addTo(map);
            
            // Add error handling
            overlay${overlay.index}.on('tileerror', function(error) {
              console.error('Multibeam tile error:', error.tile._url);
            });
            overlay${overlay.index}.isCustomTileLayer = true; // Mark as custom tile layer
            window.overlays.push(overlay${overlay.index});
            }
            `;
          } else if (overlay.type === 'tile') {
            // Handle tile layers (e.g., weather radar)
            return `
            // Keep weather imagery above the ocean basemap and its reference
            // labels. The default tile pane is below the reference layer.
            if (!map.getPane('radarPane')) {
              var radarPane = map.createPane('radarPane');
              radarPane.style.zIndex = 450;
              radarPane.style.pointerEvents = 'none';
            }
            var overlay${overlay.index} = L.tileLayer('${escapedUrl}', {
              pane: 'radarPane',
              opacity: ${overlay.opacity !== undefined ? overlay.opacity : 0.7},
              zIndex: 450,
              // RainViewer only serves native radar tiles through zoom 7. Leaflet
              // will reuse and scale the zoom-7 tile above that level rather than
              // requesting an empty unsupported tile.
              maxNativeZoom: 7,
              maxZoom: 20,
              minZoom: 3,
              attribution: 'Weather data © RainViewer',
              updateWhenZooming: true, // Update during zoom
              updateWhenIdle: false, // Don't wait for idle
              keepBuffer: 0 // Don't keep tiles outside viewport
            }).addTo(map);
            overlay${overlay.index}.on('tileerror', function(error) {
              window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'radarTileError',
                url: error.tile && error.tile.src ? error.tile.src : 'unknown'
              }));
            });
            overlay${overlay.index}.isTileLayer = true; // Mark as tile layer for identification
            window.overlays.push(overlay${overlay.index});
            `;
          } else {
            // Handle image overlays (default behavior)
            return `
            var imageBounds${overlay.index} = [[${overlay.bounds.minLat}, ${overlay.bounds.minLon}], [${overlay.bounds.maxLat}, ${overlay.bounds.maxLon}]];
            // Don't add timestamp to data URLs
            var overlayUrl = '${escapedUrl}';
            // Add timestamp only for remote URLs to bust cache
            if (!overlayUrl.startsWith('data:')) {
              overlayUrl = overlayUrl + (overlayUrl.includes('?') ? '&' : '?') + 't=' + ${Date.now()};
            }
            console.log('Adding image overlay with URL length:', overlayUrl.length);
            console.log('First 200 chars of URL:', overlayUrl.substring(0, 200));
            var overlay${overlay.index} = L.imageOverlay(overlayUrl, imageBounds${overlay.index}, {
              opacity: ${overlay.opacity !== undefined ? overlay.opacity : 1.0},
              zIndex: ${overlay.zIndex || overlay.index}
            }).addTo(map);
            
            // Add error handling for the overlay with retry
            var retryCount${overlay.index} = 0;
            var maxRetries = 3;
            
            overlay${overlay.index}.on('error', function(e) {
              console.error('Failed to load overlay ${overlay.index}:', e);
              console.error('Failed URL was:', overlayUrl);
              
              // Retry logic
              if (retryCount${overlay.index} < maxRetries) {
                retryCount${overlay.index}++;
                console.log('Retrying overlay ${overlay.index}, attempt ' + retryCount${overlay.index});
                
                // Remove the failed overlay
                map.removeLayer(overlay${overlay.index});
                
                // Try again with a fresh timestamp
                setTimeout(function() {
                  var retryUrl = overlayUrl.replace(/t=\d+/, 't=' + Date.now());
                  overlay${overlay.index} = L.imageOverlay(retryUrl, imageBounds${overlay.index}, {
                    opacity: ${overlay.opacity !== undefined ? overlay.opacity : 1.0},
                    zIndex: ${overlay.zIndex || overlay.index}
                  }).addTo(map);
                  
                  // Re-attach handlers
                  overlay${overlay.index}.on('error', arguments.callee);
                  overlay${overlay.index}.on('load', function() {
                    console.log('Successfully loaded overlay ${overlay.index} after retry');
                  });
                }, 1000 * retryCount${overlay.index}); // Exponential backoff
              } else {
                console.error('Overlay ${overlay.index} failed after ' + maxRetries + ' retries');
                // Post error message back to React Native
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  type: 'overlayError',
                  index: ${overlay.index},
                  url: overlayUrl.substring(0, 200)
                }));
              }
            });
            
            // Add load handler for debugging
            overlay${overlay.index}.on('load', function() {
              console.log('Successfully loaded overlay ${overlay.index} from URL with length:', overlayUrl.length);
            });
            
            window.overlays.push(overlay${overlay.index});
            `;
          }
        }).join('\n')}
        
        // Force map to redraw
        map.invalidateSize();
        
        true;
      `;
      
      webViewRef.current.injectJavaScript(script);
      logger.info('Injected overlays script', { 
        overlayCount: overlays.length,
        overlays: overlays.map(o => ({ 
          type: o.type,
          url: o.url.startsWith('http') ? o.url : 'data:image...', 
          zIndex: o.zIndex 
        }))
      });
    };
    
    updateOverlays();
  }, [overlays]);
  
  // Update markers dynamically without reloading the map
  useEffect(() => {
    if (!webViewRef.current) return;
    
    // Debounce marker updates to prevent flicker
    const timeoutId = setTimeout(() => {
      const updateMarkersScript = `
        (function() {
          // Store current zoom level for debugging
          var currentZoom = map.getZoom();
          console.log('Updating markers at zoom level:', currentZoom, 'Total markers to add:', ${markers.length});
          
          // Store existing marker data for comparison
          var existingMarkerCount = window.markers ? window.markers.length : 0;
          var newMarkerCount = ${markers.length};
          
          // Only update if markers have actually changed
          if (existingMarkerCount === newMarkerCount && window.lastMarkerUpdate) {
            var timeSinceLastUpdate = Date.now() - window.lastMarkerUpdate;
            if (timeSinceLastUpdate < 500) {
              console.log('Skipping marker update - too soon since last update');
              return;
            }
          }
          
          window.lastMarkerUpdate = Date.now();
          
          // Remove existing markers more carefully
          if (window.markerClusterGroup) {
            window.markerClusterGroup.clearLayers();
          }
          if (window.markers) {
            window.markers.forEach(function(marker) {
              if (map.hasLayer(marker)) {
                map.removeLayer(marker);
              }
            });
          }
          window.markers = [];
        
        ${enableClustering ? `
        // Ensure cluster group exists
        if (!window.markerClusterGroup) {
          window.markerClusterGroup = L.markerClusterGroup({
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            spiderfyOnMaxZoom: true,
            removeOutsideVisibleBounds: false,  // Keep markers in memory even when outside viewport
            animate: true,
            animateAddingMarkers: false,  // Disable animation to prevent flicker
            disableClusteringAtZoom: 13,  // Increase zoom level for disabling clustering
            maxClusterRadius: 60,  // Reduce cluster radius for better hotspot visibility
            iconCreateFunction: function(cluster) {
              var childCount = cluster.getChildCount();
              var c = ' marker-cluster-';
              if (childCount < 10) {
                c += 'small';
              } else if (childCount < 20) {
                c += 'medium';
              } else {
                c += 'large';
              }
              return new L.DivIcon({
                html: '<div><span>' + childCount + '</span></div>',
                className: 'marker-cluster' + c,
                iconSize: new L.Point(40, 40)
              });
            }
          });
          map.addLayer(window.markerClusterGroup);
        }
        ` : ''}
        
        // Add new markers
        ${markers.map((marker, index) => {
          if (marker.icon) {
            const markerCode = `
              var icon${index} = L.divIcon({
                html: '<div style="font-size: ${marker.size[0] - 8}px; width: ${marker.size[0]}px; height: ${marker.size[1]}px; display: flex; align-items: center; justify-content: center;">${marker.icon}</div>',
                iconSize: [${marker.size[0]}, ${marker.size[1]}],
                className: 'emoji-marker',
                iconAnchor: [${marker.size[0] / 2}, ${marker.size[1] / 2}]
              });
              
              // Create styled popup content
              var popupContent${index} = '';
              ${marker.type === 'buoy' && marker.buoyData ? `
              // Format buoy observation time
              var obsTime = '${marker.buoyData.observation_time}';
              var formattedTime = new Date(obsTime).toLocaleString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                hour: 'numeric', 
                minute: '2-digit',
                timeZoneName: 'short'
              });
              
              // Build buoy popup content
              var buoyContent = '';
              buoyContent += '<div style="font-family: -apple-system, BlinkMacSystemFont, \\'Segoe UI\\', Roboto, sans-serif; padding: 0; min-width: 250px; max-width: 320px;">';
              buoyContent += '<div style="background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%); color: white; padding: 12px 16px; font-size: 16px; font-weight: bold; display: flex; align-items: center; gap: 8px;">';
              buoyContent += '<span style="font-size: 20px;">🛟</span>';
              buoyContent += '<div>';
              buoyContent += '<div>${(marker.buoyData.station_name || marker.buoyData.station_id).replace(/'/g, "\\'")}</div>';
              buoyContent += '<div style="font-size: 11px; font-weight: normal; opacity: 0.9;">Station ${marker.buoyData.station_id}</div>';
              buoyContent += '</div>';
              buoyContent += '</div>';
              buoyContent += '<div style="padding: 16px;">';
              buoyContent += '<div style="font-size: 12px; color: #666; margin-bottom: 12px;">Last Reading: ' + formattedTime + '</div>';
              
              ${marker.buoyData.wind_speed !== undefined && marker.buoyData.wind_speed !== null ? `
              buoyContent += '<div style="display: flex; align-items: center; margin-bottom: 10px;">';
              buoyContent += '<span style="margin-right: 8px;">💨</span>';
              buoyContent += '<div style="flex: 1;">';
              buoyContent += '<div style="font-weight: 600; color: #333;">Wind</div>';
              buoyContent += '<div style="color: #666; font-size: 13px;">';
              buoyContent += '${marker.buoyData.wind_speed.toFixed(1)} kts';
              ${marker.buoyData.wind_direction !== undefined && marker.buoyData.wind_direction !== null ? `
              buoyContent += ' from ${marker.buoyData.wind_direction}°';
              ` : ''}
              ${marker.buoyData.wind_gust !== undefined && marker.buoyData.wind_gust !== null ? `
              buoyContent += ' (gusts ${marker.buoyData.wind_gust.toFixed(1)} kts)';
              ` : ''}
              buoyContent += '</div>';
              buoyContent += '</div>';
              buoyContent += '</div>';
              ` : ''}
              
              ${marker.buoyData.wave_height !== undefined && marker.buoyData.wave_height !== null ? `
              buoyContent += '<div style="display: flex; align-items: center; margin-bottom: 10px;">';
              buoyContent += '<span style="margin-right: 8px;">🌊</span>';
              buoyContent += '<div style="flex: 1;">';
              buoyContent += '<div style="font-weight: 600; color: #333;">Waves</div>';
              buoyContent += '<div style="color: #666; font-size: 13px;">';
              buoyContent += '${marker.buoyData.wave_height.toFixed(1)} ft';
              ${marker.buoyData.dominant_wave_period !== undefined && marker.buoyData.dominant_wave_period !== null ? `
              buoyContent += ' @ ${marker.buoyData.dominant_wave_period.toFixed(1)}s';
              ` : ''}
              ${marker.buoyData.wave_direction !== undefined && marker.buoyData.wave_direction !== null ? `
              buoyContent += ' from ${marker.buoyData.wave_direction}°';
              ` : ''}
              buoyContent += '</div>';
              buoyContent += '</div>';
              buoyContent += '</div>';
              ` : ''}
              
              ${marker.buoyData.water_temperature !== undefined && marker.buoyData.water_temperature !== null ? `
              buoyContent += '<div style="display: flex; align-items: center; margin-bottom: 10px;">';
              buoyContent += '<span style="margin-right: 8px;">🌡️</span>';
              buoyContent += '<div style="flex: 1;">';
              buoyContent += '<div style="font-weight: 600; color: #333;">Sea Temp</div>';
              buoyContent += '<div style="color: #666; font-size: 13px;">${marker.buoyData.water_temperature.toFixed(1)}°F</div>';
              buoyContent += '</div>';
              buoyContent += '</div>';
              ` : ''}
              
              ${marker.buoyData.air_temperature !== undefined && marker.buoyData.air_temperature !== null ? `
              buoyContent += '<div style="display: flex; align-items: center; margin-bottom: 10px;">';
              buoyContent += '<span style="margin-right: 8px;">🌡️</span>';
              buoyContent += '<div style="flex: 1;">';
              buoyContent += '<div style="font-weight: 600; color: #333;">Air Temp</div>';
              buoyContent += '<div style="color: #666; font-size: 13px;">${marker.buoyData.air_temperature.toFixed(1)}°F</div>';
              buoyContent += '</div>';
              buoyContent += '</div>';
              ` : ''}
              
              ${marker.buoyData.sea_level_pressure !== undefined && marker.buoyData.sea_level_pressure !== null ? `
              buoyContent += '<div style="display: flex; align-items: center; margin-bottom: 10px;">';
              buoyContent += '<span style="margin-right: 8px;">🔵</span>';
              buoyContent += '<div style="flex: 1;">';
              buoyContent += '<div style="font-weight: 600; color: #333;">Pressure</div>';
              buoyContent += '<div style="color: #666; font-size: 13px;">${marker.buoyData.sea_level_pressure.toFixed(1)} mb</div>';
              buoyContent += '</div>';
              buoyContent += '</div>';
              ` : ''}
              
              buoyContent += '<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e0e0; font-size: 11px; color: #999;">';
              buoyContent += '${marker.position.lat.toFixed(3)}°, ${marker.position.lng.toFixed(3)}°';
              buoyContent += '</div>';
              buoyContent += '</div>';
              buoyContent += '</div>';
              
              popupContent${index} = buoyContent;
              ` : marker.type === 'hotspot' ? generateHotspotPopupContent(marker, index) : marker.type === 'hotspot_old' ? `
              popupContent${index} = '<div style="' +
                'font-family: -apple-system, BlinkMacSystemFont, \\'Segoe UI\\', Roboto, sans-serif;' +
                'padding: 0;' +
                'min-width: 250px;' +
                'max-width: 300px;' +
                '">' +
                '<div style="' +
                'background: linear-gradient(135deg, #FF6B6B 0%, #FF5757 100%);' +
                'color: white;' +
                'padding: 16px 20px;' +
                'font-size: 18px;' +
                'font-weight: 600;' +
                'border-radius: 12px 12px 0 0;' +
                'display: flex;' +
                'align-items: center;' +
                'gap: 10px;' +
                '">' +
                '<span style="font-size: 24px;">🎯</span>' +
                '<span>${marker.title.replace(/'/g, "\\'")}</span>' +
                '</div>' +
                '<div style="' +
                'background: white;' +
                'padding: 16px 20px;' +
                'border-radius: 0 0 12px 12px;' +
                'box-shadow: 0 2px 8px rgba(0,0,0,0.1);' +
                '">' +
                ${marker.temperature !== undefined ? `
                '<div style="' +
                'display: flex;' +
                'align-items: center;' +
                'gap: 8px;' +
                'margin-bottom: 12px;' +
                '">' +
                '<span style="font-size: 16px;">🌡️</span>' +
                '<div>' +
                '<div style="' +
                'color: #666;' +
                'font-size: 12px;' +
                'font-weight: 500;' +
                'text-transform: uppercase;' +
                'letter-spacing: 0.5px;' +
                '">Temperature</div>' +
                '<div style="' +
                'color: #333;' +
                'font-size: 16px;' +
                'font-weight: 600;' +
                '">${((marker.temperature * 9/5) + 32).toFixed(1)}°F</div>' +
                '</div>' +
                '</div>' +
                ` : ''}
                ${marker.chlorophyll !== undefined ? `
                '<div style="' +
                'display: flex;' +
                'align-items: center;' +
                'gap: 8px;' +
                'margin-bottom: 12px;' +
                '">' +
                '<span style="font-size: 16px;">🌿</span>' +
                '<div>' +
                '<div style="' +
                'color: #666;' +
                'font-size: 12px;' +
                'font-weight: 500;' +
                'text-transform: uppercase;' +
                'letter-spacing: 0.5px;' +
                '">Chlorophyll</div>' +
                '<div style="' +
                'color: #333;' +
                'font-size: 16px;' +
                'font-weight: 600;' +
                '">${marker.chlorophyll.toFixed(3)} mg/m³</div>' +
                '</div>' +
                '</div>' +
                ` : ''}
                ${marker.currentSpeed !== undefined ? `
                '<div style="' +
                'display: flex;' +
                'align-items: center;' +
                'gap: 8px;' +
                'margin-bottom: 12px;' +
                '">' +
                '<span style="font-size: 16px;">💨</span>' +
                '<div>' +
                '<div style="' +
                'color: #666;' +
                'font-size: 12px;' +
                'font-weight: 500;' +
                'text-transform: uppercase;' +
                'letter-spacing: 0.5px;' +
                '">Current</div>' +
                '<div style="' +
                'color: #333;' +
                'font-size: 16px;' +
                'font-weight: 600;' +
                '">${marker.currentSpeed.toFixed(1)} kts${marker.currentDirection !== undefined ? " @ " + Math.round(marker.currentDirection) + "°" : ""}</div>' +
                '</div>' +
                '</div>' +
                ` : ''}
                '<div style="' +
                'border-top: 1px solid #eee;' +
                'padding-top: 12px;' +
                'margin-top: 12px;' +
                '">' +
                '<div style="' +
                'color: #666;' +
                'font-size: 12px;' +
                'font-weight: 500;' +
                'text-transform: uppercase;' +
                'letter-spacing: 0.5px;' +
                'margin-bottom: 4px;' +
                '">📍 Coordinates</div>' +
                '<div style="' +
                'color: #333;' +
                'font-size: 14px;' +
                'font-weight: 500;' +
                '">${marker.position.lat.toFixed(6)}°, ${marker.position.lng.toFixed(6)}°</div>' +
                '</div>' +
                '</div>' +
                '</div>';
              ` : marker.type === 'waypoint' ? `
              popupContent${index} = '<div style="' +
                'font-family: -apple-system, BlinkMacSystemFont, \\'Segoe UI\\', Roboto, sans-serif;' +
                'padding: 0;' +
                'min-width: 200px;' +
                'max-width: 280px;' +
                '">' +
                '<div style="' +
                'background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);' +
                'color: white;' +
                'padding: 16px 20px;' +
                'font-size: 18px;' +
                'font-weight: 600;' +
                'border-radius: 12px 12px 0 0;' +
                'display: flex;' +
                'align-items: center;' +
                'gap: 10px;' +
                '">' +
                '<span style="font-size: 24px;">${marker.icon}</span>' +
                '<span>${marker.title.replace(/'/g, "\\'")}</span>' +
                '</div>' +
                '<div style="' +
                'background: white;' +
                'padding: 16px 20px;' +
                'border-radius: 0 0 12px 12px;' +
                'box-shadow: 0 2px 8px rgba(0,0,0,0.1);' +
                '">' +
                '<div style="' +
                'color: #666;' +
                'font-size: 13px;' +
                'font-weight: 500;' +
                'text-transform: uppercase;' +
                'letter-spacing: 0.5px;' +
                'margin-bottom: 8px;' +
                '">📍 Coordinates</div>' +
                '<div style="' +
                'color: #333;' +
                'font-size: 15px;' +
                'font-weight: 500;' +
                'margin-bottom: 4px;' +
                '">${marker.position.lat.toFixed(6)}°, ${marker.position.lng.toFixed(6)}°</div>' +
                '<div style="' +
                'color: #666;' +
                'font-size: 13px;' +
                'font-style: italic;' +
                '">Tap and hold to save waypoint</div>' +
                '</div>' +
                '</div>';
              ` : `
              popupContent${index} = '<div style="' +
                'font-family: -apple-system, BlinkMacSystemFont, \\'Segoe UI\\', Roboto, sans-serif;' +
                'padding: 0;' +
                'min-width: 200px;' +
                'max-width: 280px;' +
                '">' +
                '<div style="' +
                'background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);' +
                'color: white;' +
                'padding: 16px 20px;' +
                'font-size: 18px;' +
                'font-weight: 600;' +
                'border-radius: 12px 12px 0 0;' +
                'display: flex;' +
                'align-items: center;' +
                'gap: 10px;' +
                '">' +
                '<span style="font-size: 24px;">${marker.icon}</span>' +
                '<span>${marker.title.replace(/'/g, "\\'")}</span>' +
                '</div>' +
                '<div style="' +
                'background: white;' +
                'padding: 16px 20px;' +
                'border-radius: 0 0 12px 12px;' +
                '">' +
                '<div style="' +
                'color: #666;' +
                'font-size: 14px;' +
                '">Location marked</div>' +
                '</div>' +
                '</div>';
              `}
              
              var marker${index} = L.marker([${marker.position.lat}, ${marker.position.lng}], { icon: icon${index} })
                .bindPopup(popupContent${index}, {
                  maxWidth: 280,
                  className: 'custom-popup'
                })
                .on('click', function() {
                  window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'markerClick',
                    position: { lat: ${marker.position.lat}, lng: ${marker.position.lng} }
                  }));
                });
              
              window.markers.push(marker${index});
              
              // Force marker to be visible
              marker${index}.setOpacity(1);
              `;
            
            if (enableClustering && !marker.noCluster) {
              return markerCode + `
              // Add to cluster group
              try {
                window.markerClusterGroup.addLayer(marker${index});
                // Ensure marker is tracked
                if (!window.markerClusterGroup.hasLayer(marker${index})) {
                  console.warn('Failed to add marker ${index} to cluster group, adding directly to map');
                  marker${index}.addTo(map);
                }
              } catch(e) {
                console.error('Error adding marker ${index} to cluster:', e);
                marker${index}.addTo(map);
              }`;
            } else {
              return markerCode + `
              // Add directly to map
              try {
                marker${index}.addTo(map);
                // Verify marker was added
                if (!map.hasLayer(marker${index})) {
                  console.warn('Failed to add marker ${index} to map, retrying...');
                  setTimeout(function() {
                    marker${index}.addTo(map);
                  }, 100);
                }
              } catch(e) {
                console.error('Error adding marker ${index} to map:', e);
              }`;
            }
          } else {
            const markerCode = `
              // Create styled popup for standard marker
              var popupContent${index} = '<div style="' +
                'font-family: -apple-system, BlinkMacSystemFont, \\'Segoe UI\\', Roboto, sans-serif;' +
                'padding: 0;' +
                'min-width: 200px;' +
                'max-width: 280px;' +
                '">' +
                '<div style="' +
                'background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);' +
                'color: white;' +
                'padding: 16px 20px;' +
                'font-size: 18px;' +
                'font-weight: 600;' +
                'border-radius: 12px 12px 0 0;' +
                '">' +
                '${marker.title.replace(/'/g, "\\'")}' +
                '</div>' +
                '<div style="' +
                'background: white;' +
                'padding: 16px 20px;' +
                'border-radius: 0 0 12px 12px;' +
                'box-shadow: 0 2px 8px rgba(0,0,0,0.1);' +
                '">' +
                '<div style="' +
                'color: #666;' +
                'font-size: 13px;' +
                'font-weight: 500;' +
                'text-transform: uppercase;' +
                'letter-spacing: 0.5px;' +
                'margin-bottom: 8px;' +
                '">📍 Coordinates</div>' +
                '<div style="' +
                'color: #333;' +
                'font-size: 15px;' +
                'font-weight: 500;' +
                '">${marker.position.lat.toFixed(6)}°, ${marker.position.lng.toFixed(6)}°</div>' +
                '</div>' +
                '</div>';
              
              var marker${index} = L.marker([${marker.position.lat}, ${marker.position.lng}])
                .bindPopup(popupContent${index}, {
                  maxWidth: 280,
                  className: 'custom-popup'
                })
                .on('click', function() {
                  window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'markerClick',
                    position: { lat: ${marker.position.lat}, lng: ${marker.position.lng} }
                  }));
                });
              
              window.markers.push(marker${index});
              
              // Force marker to be visible
              marker${index}.setOpacity(1);
              `;
            
            if (enableClustering && !marker.noCluster) {
              return markerCode + `
              // Add to cluster group
              try {
                window.markerClusterGroup.addLayer(marker${index});
                // Ensure marker is tracked
                if (!window.markerClusterGroup.hasLayer(marker${index})) {
                  console.warn('Failed to add marker ${index} to cluster group, adding directly to map');
                  marker${index}.addTo(map);
                }
              } catch(e) {
                console.error('Error adding marker ${index} to cluster:', e);
                marker${index}.addTo(map);
              }`;
            } else {
              return markerCode + `
              // Add directly to map
              try {
                marker${index}.addTo(map);
                // Verify marker was added
                if (!map.hasLayer(marker${index})) {
                  console.warn('Failed to add marker ${index} to map, retrying...');
                  setTimeout(function() {
                    marker${index}.addTo(map);
                  }, 100);
                }
              } catch(e) {
                console.error('Error adding marker ${index} to map:', e);
              }`;
            }
          }
        }).join('\n')}
        
          // Force refresh of cluster group if it exists
          if (window.markerClusterGroup) {
            // Force the cluster group to recalculate clusters
            window.markerClusterGroup.refreshClusters();
            
            // Make sure cluster group is added to map
            if (!map.hasLayer(window.markerClusterGroup)) {
              map.addLayer(window.markerClusterGroup);
            }
            
            // Ensure all markers are properly displayed at current zoom
            window.markerClusterGroup.eachLayer(function(marker) {
              if (marker._icon) {
                marker._icon.style.opacity = 1;
                marker._icon.style.display = 'block';
                marker._icon.style.visibility = 'visible';
              }
            });
          }
          
          // Also ensure non-clustered markers are visible
          window.markers.forEach(function(marker) {
            if (!window.markerClusterGroup || !window.markerClusterGroup.hasLayer(marker)) {
              if (!map.hasLayer(marker)) {
                marker.addTo(map);
              }
              if (marker._icon) {
                marker._icon.style.opacity = 1;
                marker._icon.style.display = 'block';
                marker._icon.style.visibility = 'visible';
              }
            }
          });
          
          // Log marker count for debugging
          console.log('Markers update complete. Total markers:', window.markers.length);
          if (window.markerClusterGroup) {
            console.log('Markers in cluster group:', window.markerClusterGroup.getLayers().length);
          }
          
          // Force map to update its display
          map.invalidateSize(false);
          
          // Call refresh to ensure everything is visible
          if (window.refreshMarkers) {
            setTimeout(function() {
              window.refreshMarkers();
            }, 50);
          }
        })();
      `;
      
      webViewRef.current?.injectJavaScript(updateMarkersScript);
    }, 100); // 100ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [markers, enableClustering]);
  
  // Update shapes (polylines) when props change
  useEffect(() => {
    if (webViewRef.current) {
      const script = `
        // Remove all existing shapes
        if (window.shapes) {
          window.shapes.forEach(shape => shape.remove());
        }
        window.shapes = [];
        
        // Add new shapes
        ${shapes.map((shape, index) => {
          if (shape.shapeType === 'polyline' && shape.positions.length > 1) {
            // Calculate distances between points
            const distanceTexts = [];
            if (shape.positions.length === 2) {
              // For single segment, calculate distance
              const lat1 = shape.positions[0].lat;
              const lng1 = shape.positions[0].lng;
              const lat2 = shape.positions[1].lat;
              const lng2 = shape.positions[1].lng;
              
              // Haversine formula for distance in miles
              const R = 3959; // Earth's radius in miles
              const dLat = (lat2 - lat1) * Math.PI / 180;
              const dLng = (lng2 - lng1) * Math.PI / 180;
              const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                        Math.sin(dLng/2) * Math.sin(dLng/2);
              const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
              const distance = R * c;
              
              // Add distance marker at midpoint
              const midLat = (lat1 + lat2) / 2;
              const midLng = (lng1 + lng2) / 2;
              distanceTexts.push(`
                var distanceMarker = L.marker([${midLat}, ${midLng}], {
                  icon: L.divIcon({
                    className: 'distance-label',
                    html: '<div style="background: white; padding: 6px 10px; border-radius: 4px; border: 2px solid #000000; font-weight: bold; font-size: 14px; color: #000000; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">${distance.toFixed(2)} mi</div>',
                    iconSize: [100, 30],
                    iconAnchor: [50, 15]
                  })
                }).addTo(map);
                window.shapes.push(distanceMarker);
              `);
            }
            
            return `
              var polyline${index} = L.polyline([
                ${shape.positions.map(pos => `[${pos.lat}, ${pos.lng}]`).join(',')}
              ], {
                color: '${shape.color || '#FF0000'}',
                weight: ${shape.weight || 3},
                opacity: 1.0
              }).addTo(map);
              window.shapes.push(polyline${index});
              ${distanceTexts.join('')}
            `;
          }
          return '';
        }).join('\n')}
        
        true;
      `;
      
      webViewRef.current.injectJavaScript(script);
    }
  }, [shapes]);
  
  // Update map center and zoom when props change
  useEffect(() => {
    if (webViewRef.current) {
      // Debounce map updates to prevent rapid changes
      const timeoutId = setTimeout(() => {
        const script = `
          if (window.updateMapView) {
            window.updateMapView(${safeCenter.lat}, ${safeCenter.lng}, ${safeZoom});
          }
          true;
        `;
        webViewRef.current?.injectJavaScript(script);
      }, 50); // Small delay to batch updates
      
      return () => clearTimeout(timeoutId);
    }
  }, [safeCenter.lat, safeCenter.lng, safeZoom]);

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <link rel="stylesheet" href="https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css" />
      <link rel="stylesheet" href="https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css" />
      <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
      <script src="https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js"></script>
      <style>
        body { margin: 0; padding: 0; }
        #map { height: 100vh; width: 100vw; }
        .emoji-marker {
          background: transparent;
          border: none;
        }
        /* Custom popup styling for high-end appearance */
        .leaflet-popup {
          margin-bottom: 20px;
        }
        .leaflet-popup-content-wrapper {
          padding: 0;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2);
          overflow: hidden;
        }
        .leaflet-popup-content {
          margin: 0;
          line-height: 1.4;
          font-size: 14px;
          min-width: 200px;
        }
        .leaflet-popup-tip-container {
          display: none;
        }
        .leaflet-popup-close-button {
          color: white !important;
          font-size: 20px !important;
          font-weight: bold !important;
          top: 10px !important;
          right: 10px !important;
          width: 24px !important;
          height: 24px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: rgba(255,255,255,0.2) !important;
          border-radius: 50% !important;
        }
        .leaflet-popup-close-button:hover {
          background: rgba(255,255,255,0.3) !important;
        }
        /* Custom cluster styles for fishing spots */
        .marker-cluster-small {
          background-color: rgba(33, 150, 243, 0.6);
        }
        .marker-cluster-small div {
          background-color: rgba(33, 150, 243, 0.8);
          color: white;
          font-weight: bold;
        }
        .marker-cluster-medium {
          background-color: rgba(33, 150, 243, 0.7);
        }
        .marker-cluster-medium div {
          background-color: rgba(33, 150, 243, 0.85);
          color: white;
          font-weight: bold;
        }
        .marker-cluster-large {
          background-color: rgba(33, 150, 243, 0.8);
        }
        .marker-cluster-large div {
          background-color: rgba(33, 150, 243, 0.9);
          color: white;
          font-weight: bold;
        }
        /* Custom popup styles */
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 3px 10px rgba(0,0,0,0.2);
          padding: 0;
        }
        .leaflet-popup-content {
          margin: 0;
          line-height: 1.4;
        }
        .leaflet-popup-tip {
          background: white;
          box-shadow: 0 3px 10px rgba(0,0,0,0.1);
        }
      </style>
    </head>
    <body>
      <div id="map"></div>
      <script>
        // Global error handler
        window.onerror = function(msg, url, lineNo, columnNo, error) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'error',
            message: msg,
            source: url,
            lineno: lineNo,
            colno: columnNo,
            error: error ? error.toString() : 'Unknown error'
          }));
          return true;
        };
        
        try {
          var map = L.map('map', {
            zoomControl: false,
            attributionControl: false,
            maxZoom: 20,  // Allow deep zoom
            minZoom: 3,
            // Improve desktop interaction
            dragging: true,
            touchZoom: 'center',
            scrollWheelZoom: true,
            doubleClickZoom: true,
            boxZoom: true,
            keyboard: true,
            zoomAnimation: true,
            zoomAnimationThreshold: 4,
            fadeAnimation: true,
            markerZoomAnimation: true,
            // Prevent touch event conflicts on desktop
            tap: false,
            tapTolerance: 15,
            bounceAtZoomLimits: false,
            // Better handling for desktop mouse events
            wheelDebounceTime: 40,
            wheelPxPerZoomLevel: 60,
            // Keep map state stable
            inertia: true,
            inertiaDeceleration: 3000,
            inertiaMaxSpeed: Infinity,
            easeLinearity: 0.2,
            worldCopyJump: false,
            maxBoundsViscosity: 0.0
          }).setView([${safeCenter.lat}, ${safeCenter.lng}], ${safeZoom});
          
          // Use Esri Ocean base + Ocean Reference for maritime features
          var baseLayer;
          
          // Esri Ocean base map
          baseLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}', {
            attribution: 'Esri, GEBCO, NOAA, National Geographic',
            maxZoom: 20,
            maxNativeZoom: 13
          }).addTo(map);
          
          // Ocean Reference layer - includes cities, maritime features, canyons, banks, etc.
          var labelsLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Reference/MapServer/tile/{z}/{y}/{x}', {
            maxZoom: 20,
            maxNativeZoom: 13,
            opacity: 1.0,
            zIndex: 1000
          }).addTo(map);
          
          // Keep track of layers
          window.baseLayer = baseLayer;
          window.labelsLayer = labelsLayer;
          window.currentBaseMapType = 'ocean';
        
        window.overlays = [];
        window.shapes = [];
        window.markers = [];
        
        // Global function to refresh all markers
        window.refreshMarkers = function() {
          console.log('Refreshing all markers...');
          
          if (window.markerClusterGroup) {
            // Ensure cluster group is on map
            if (!map.hasLayer(window.markerClusterGroup)) {
              map.addLayer(window.markerClusterGroup);
            }
            window.markerClusterGroup.refreshClusters();
            
            // Check each marker in cluster
            window.markerClusterGroup.eachLayer(function(marker) {
              if (marker._icon) {
                marker._icon.style.opacity = 1;
                marker._icon.style.display = 'block';
                marker._icon.style.visibility = 'visible';
              }
            });
          }
          
          // Check non-clustered markers
          if (window.markers) {
            window.markers.forEach(function(marker, idx) {
              if (!window.markerClusterGroup || !window.markerClusterGroup.hasLayer(marker)) {
                if (!map.hasLayer(marker)) {
                  console.log('Re-adding non-clustered marker', idx);
                  marker.addTo(map);
                }
                if (marker._icon) {
                  marker._icon.style.opacity = 1;
                  marker._icon.style.display = 'block';
                  marker._icon.style.visibility = 'visible';
                }
              }
            });
          }
          
          console.log('Marker refresh complete');
        };
        
        // Markers will be added dynamically via separate effect
        
        // Handle map clicks
        map.on('click', function(e) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'mapClick',
            lat: e.latlng.lat,
            lng: e.latlng.lng
          }));
        });

        // Handle map long press (contextmenu)
        map.on('contextmenu', function(e) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'mapLongPress',
            lat: e.latlng.lat,
            lng: e.latlng.lng
          }));
        });
        
        // Handle map move with debounce
        var moveTimer = null;
        function sendMapUpdate() {
          var center = map.getCenter();
          var bounds = map.getBounds();
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'mapMove',
            center: { lat: center.lat, lng: center.lng },
            zoom: map.getZoom(),
            bounds: {
              minLat: bounds.getSouth(),
              maxLat: bounds.getNorth(),
              minLon: bounds.getWest(),
              maxLon: bounds.getEast()
            }
          }));
        }
        
        // Listen to both move and zoom events
        map.on('move zoom', function() {
          if (moveTimer) clearTimeout(moveTimer);
          moveTimer = setTimeout(function() {
            sendMapUpdate();
            // Also refresh markers after movement
            if (window.refreshMarkers) {
              window.refreshMarkers();
            }
          }, 300); // Debounce for 300ms
        });
        
        // Add zoom start event to prepare markers
        map.on('zoomstart', function() {
          // Ensure markers stay visible during zoom animation
          if (window.markerClusterGroup) {
            window.markerClusterGroup.eachLayer(function(marker) {
              if (marker._icon) {
                marker._icon.style.opacity = 1;
                marker._icon.style.visibility = 'visible';
              }
            });
          }
        });
        
        // Also send immediate update on zoom end for better responsiveness
        map.on('zoomend', function() {
          if (moveTimer) clearTimeout(moveTimer);
          sendMapUpdate();
          
          // Force tile layers to reload after zoom
          window.overlays.forEach(function(overlay) {
            if (overlay.isTileLayer || overlay.isCustomTileLayer) {
              // Force complete tile refresh
              if (overlay._removeAllTiles) {
                // Remove all tiles first to force reload
                overlay._removeAllTiles();
                overlay._update();
              } else if (overlay.redraw) {
                overlay.redraw();
              } else if (overlay._resetView) {
                // For custom tile layers, reset the view to force new tile requests
                overlay._resetView();
                overlay._update();
              }
            }
          });
          
          // Refresh cluster markers on zoom end to ensure they're visible
          if (window.markerClusterGroup) {
            console.log('Refreshing clusters after zoom to level:', map.getZoom());
            window.markerClusterGroup.refreshClusters();
            
            // Force redraw of all markers
            window.markerClusterGroup.eachLayer(function(marker) {
              if (marker._icon) {
                marker._icon.style.opacity = 1;
                marker._icon.style.display = 'block';
                // Force browser to repaint
                marker._icon.style.transform = marker._icon.style.transform || 'translateZ(0)';
              }
            });
          }
          
          // Also check non-clustered markers
          if (window.markers) {
            window.markers.forEach(function(marker) {
              if (marker._icon) {
                marker._icon.style.opacity = 1;
                marker._icon.style.display = 'block';
                marker._icon.style.transform = marker._icon.style.transform || 'translateZ(0)';
              }
            });
          }
        });
        
        // Force bathymetry tiles to refresh on pan/drag end
        map.on('moveend', function() {
          // Check if we have custom tile layers (bathymetry)
          window.overlays.forEach(function(overlay) {
            if (overlay.isCustomTileLayer) {
              // Update tiles for custom layers like bathymetry
              if (overlay._update) {
                overlay._update();
              }
            }
          });
          
          // Ensure markers remain visible after pan
          if (window.markerClusterGroup) {
            // Check if cluster group is still on map
            if (!map.hasLayer(window.markerClusterGroup)) {
              console.log('Re-adding cluster group to map after pan');
              map.addLayer(window.markerClusterGroup);
            }
            
            // Force refresh clusters
            window.markerClusterGroup.refreshClusters();
          }
          
          // Check all markers are still visible
          if (window.markers) {
            window.markers.forEach(function(marker) {
              // For non-clustered markers
              if (!window.markerClusterGroup || !window.markerClusterGroup.hasLayer(marker)) {
                if (!map.hasLayer(marker)) {
                  console.log('Re-adding marker to map after pan');
                  marker.addTo(map);
                }
              }
            });
          }
        });
        
        // Update map view
        window.updateMapView = function(lat, lng, zoom) {
          var currentZoom = map.getZoom();
          var currentCenter = map.getCenter();
          
          // Only update if there's a significant change to prevent fighting with user interaction
          var latDiff = Math.abs(currentCenter.lat - lat);
          var lngDiff = Math.abs(currentCenter.lng - lng);
          var zoomDiff = Math.abs(currentZoom - zoom);
          
          // Don't update if the user is actively interacting with the map
          if (map._animatingZoom || map._panAnim && map._panAnim._inProgress) {
            console.log('Skipping map update - animation in progress');
            return;
          }
          
          // Only update if there's a meaningful change
          if (latDiff > 0.00001 || lngDiff > 0.00001 || zoomDiff > 0.01) {
            // Use flyTo for smoother transitions on desktop
            if (zoomDiff > 0.5) {
              map.flyTo([lat, lng], zoom, {
                duration: 0.5,
                easeLinearity: 0.25
              });
            } else {
              map.setView([lat, lng], zoom, {
                animate: false
              });
            }
            
            // If zoom changed, force tile layers to reload
            if (zoomDiff > 0.01) {
              window.overlays.forEach(function(overlay) {
                if (overlay.isTileLayer || overlay.isCustomTileLayer) {
                  // Force refresh of all tile layers
                  if (overlay.redraw) {
                    overlay.redraw();
                  } else if (overlay._resetView) {
                    overlay._resetView();
                    overlay._update();
                  }
                }
              });
            }
          }
        };
        } catch (e) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'error',
            message: 'Map initialization error: ' + e.message,
            error: e.toString()
          }));
        }
      </script>
    </body>
    </html>
  `;

  const handleMessage = (event: any) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (data.type === 'error') {
        logger.error('WebView JavaScript error', data);
      } else if (data.type === 'overlayError') {
        logger.error('Overlay failed to load after retries', new Error('Overlay load failed'), { 
          index: data.index, 
          urlStart: data.url 
        });
      } else if (data.type === 'mapClick' && onMapClick) {
        onMapClick(data.lat, data.lng);
      } else if (data.type === 'mapLongPress' && onMapLongPress) {
        onMapLongPress(data.lat, data.lng);
      } else if (data.type === 'markerClick' && onMarkerClick) {
        onMarkerClick(data.position);
      } else if (data.type === 'mapMove' && onMapMove) {
        onMapMove(data.center, data.zoom, data.bounds);
      }
    } catch (error) {
      logger.error('Error handling WebView message', error instanceof Error ? error : new Error(String(error)));
    }
  };

  return (
    <View style={styles.container}>
      <WebView
        ref={webViewRef}
        source={{ html }}
        style={styles.webview}
        onMessage={handleMessage}
        javaScriptEnabled={true}
        domStorageEnabled={true}
        startInLoadingState={true}
        mixedContentMode="compatibility"
        originWhitelist={['*']}
        allowFileAccess={true}
        allowFileAccessFromFileURLs={true}
        allowUniversalAccessFromFileURLs={true}
        onError={(syntheticEvent) => {
          const { nativeEvent } = syntheticEvent;
          logger.error('WebView error', new Error(nativeEvent.description || 'WebView error'), { code: nativeEvent.code });
        }}
        onHttpError={(syntheticEvent) => {
          const { nativeEvent } = syntheticEvent;
          logger.error('WebView HTTP error', new Error(`HTTP ${nativeEvent.statusCode}: ${nativeEvent.description}`), { 
            url: nativeEvent.url,
            statusCode: nativeEvent.statusCode 
          });
        }}
        onNavigationStateChange={(navState) => {
          // Prevent navigation to external URLs
          if (navState.url && !navState.url.startsWith('about:')) {
            logger.warn('Preventing navigation to:', { url: navState.url });
            if (webViewRef.current) {
              webViewRef.current.stopLoading();
            }
          }
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webview: {
    flex: 1,
  },
});
